import { PACK_BY_ID, PACKS_FULL } from './packData.js'
import { packRecipeCount } from './packRecipeCount.js'

/** Pack id/slug stamped on saved lists when added from a curated pack. */
export function getSavedListPackId(list) {
  const meta = list?.list_metadata && typeof list.list_metadata === 'object' ? list.list_metadata : {}
  return meta.added_from_pack_id || meta.pack_id || meta.added_from_pack_slug || null
}

/**
 * Fisher–Yates shuffle (copy). Used to randomize Add-tab pack order so
 * position bias does not skew interest / apply rates.
 */
export function shufflePacks(packs) {
  const arr = Array.isArray(packs) ? [...packs] : []
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[arr[i], arr[j]] = [arr[j], arr[i]]
  }
  return arr
}

/**
 * Stable display order for Add Recipes: shuffle once per pack-id set.
 * Reuses `orderIdsRef` so re-renders (e.g. after applying a pack) do not reshuffle.
 *
 * @param {object[]} packs
 * @param {{ current: string[] | null }} orderIdsRef
 * @returns {object[]}
 */
export function orderPacksForAddDisplay(packs, orderIdsRef) {
  const list = Array.isArray(packs) ? packs : []
  const ids = list.map((p) => p.id).filter(Boolean)
  const prev = orderIdsRef?.current
  const sameSet =
    Array.isArray(prev) &&
    prev.length === ids.length &&
    ids.every((id) => prev.includes(id))

  if (!sameSet) {
    if (orderIdsRef) {
      orderIdsRef.current = shufflePacks(ids)
    }
  }

  const order = orderIdsRef?.current
  if (!Array.isArray(order) || order.length === 0) {
    return list
  }

  const byId = Object.fromEntries(list.map((p) => [p.id, p]))
  return order.map((id) => byId[id]).filter(Boolean)
}

/**
 * Merge GET /curated-packs shelf rows with local pack catalog (blurbs, recipe rows for preview).
 * When `livePacks` is null/empty, returns full PACKS_FULL objects.
 */
export function resolveCuratedPacks(livePacks) {
  if (!Array.isArray(livePacks) || livePacks.length === 0) {
    return PACKS_FULL
  }

  return livePacks.map((lp) => {
    const local = PACK_BY_ID[lp.id] || PACK_BY_ID[lp.slug] || {}
    const merged = {
      ...local,
      id: lp.id,
      slug: lp.slug || lp.id,
      name: lp.name || local.name,
      blurb: local.blurb || lp.blurb || '',
      tag: local.tag || lp.tag,
      swatches: lp.swatches?.length ? lp.swatches : local.swatches || [],
      recipe_ids: lp.recipe_ids || (Array.isArray(local.recipes) ? local.recipes.map((r) => r.id).filter(Boolean) : []),
      recipes: local.recipes || [],
    }
    const recipeCount =
      typeof lp.n === 'number' ? lp.n : packRecipeCount(merged)

    return {
      ...merged,
      n: recipeCount,
    }
  })
}

/** Set of pack id/slug values already present in the user's recipe library. */
export function deriveAddedPackIds(recipeLists, resolvedPacks) {
  const packByKey = Object.fromEntries(
    resolvedPacks.flatMap((p) => [
      [p.id, p],
      [p.slug, p],
    ].filter(([key]) => key)),
  )

  const ids = new Set()
  for (const list of recipeLists || []) {
    const raw = String(getSavedListPackId(list) || '').trim()
    if (!raw) continue
    const pack = packByKey[raw]
    ids.add(raw)
    if (pack?.id) ids.add(pack.id)
    if (pack?.slug) ids.add(pack.slug)
  }
  return ids
}
